"use client"

import * as React from "react"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { ChevronDownIcon } from "lucide-react"
import { useIntl } from "react-intl"
import { useSalonDateFnsLocale } from "@/hooks/me/use-salon"

interface DatePickerProps {
  id?: string
  date?: Date | null | undefined
  onDateChange?: (date: Date | null | undefined) => void
  placeholder?: string
  disabled?: boolean
  dateFormat?: "long" | "short"
  calendarDisabled?: React.ComponentProps<typeof Calendar>["disabled"]
}

export function DatePicker({ 
  id, 
  date, 
  onDateChange, 
  placeholder = "Select date",
  disabled = false,
  dateFormat = "short",
  calendarDisabled,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false)
  const intl = useIntl()
  const locale = useSalonDateFnsLocale()

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          id={id}
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          {date ? intl.formatDate(date, { format: dateFormat }) : placeholder}
          <ChevronDownIcon />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto overflow-hidden p-0" align="start">
        <Calendar
          mode="single"
          locale={locale}
          selected={date || undefined}
          captionLayout="dropdown"
          defaultMonth={date || undefined}
          disabled={calendarDisabled}
          onSelect={(selectedDate) => {
            onDateChange?.(selectedDate || null)
            setOpen(false)
          }}
        />
      </PopoverContent>
    </Popover>
  )
}
